import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal,
  Dropdown,
  Tab,
  Tabs,
  InputGroup,
  Row,
  Col
} from "react-bootstrap";
import { useTheme } from "../ThemeContext";
import {
  Mail,
  Send,
  FileText,
  Target,
  Plus,
  Folder,
  Edit2,
  Trash,
  Save,
  Eye,
  Check,
  Clock,
  RefreshCw,
  Download,
  Users,
  Shield,
  AlertTriangle,
  X,
  Copy,
  BarChart2,
  Activity,
  User,
  Home as Building,
  ChevronDown,
  ChevronUp,
  Monitor
} from "react-feather";
import { apifetch } from '../services/interceptors';
import { INTERNAL_REPORT_PREVIEW_KEY } from './InternalReportPreview';
import generateSecurityPDFSlide  from './NpdfGenerator' 
import generateSecurityPDF from './pdfGenerator';
import "./ProspectingMode.css";
import MODULES from "./ExternalRiskModules";
import PublicIpOptions from "./PublicIpOptions";
import { publicIpRequest } from "../utils/publicIpTargets";


const DEFAULT_PROSPECT_TEMPLATE = {
  id: 'default',
  name: 'Default Prospect Template',
  subject: 'Security Assessment Report for {domain}',
  body: `Dear {prospectName},

We have completed a security assessment for {domain}. Please find the detailed report below.

Executive Summary:
- Overall Security Score: {riskScore}/100
- Risk Level: {riskLevel}
- Total Findings: {totalFindings}

Key Findings:
{findingsSummary}

The report contains a comprehensive analysis of:
• DNS Configuration
• Email Security (SPF, DMARC)
• SSL/TLS Setup
• Security Headers
• Infrastructure Security
• Typosquatting Analysis

You can download the full PDF report using the link below:
{downloadLink}

For a complete breakdown of all findings and remediation recommendations, please review the report.

If you have any questions or would like to discuss the findings in detail, please don't hesitate to reach out.

Best regards,
Security Team`
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ProspectingMode = () => {
  const { isDarkMode } = useTheme();
  const token = localStorage.getItem("token");

  // Scan States
  const [domain, setDomain] = useState("");
  const [publicIp, setPublicIp] = useState("");
  const [publicIpMode, setPublicIpMode] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [modules, setModules] = useState([]);
  
  const [scanHistory, setScanHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("new-scan");
  const [scanId, setScanId] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // How the prospect will see this report. Seeded from the tenant's Customer Report Style
  // (Admin Panel → Tenant) but chosen per scan, since each prospect is a different pitch.
  // Whatever is selected here is stored with the scan and wins from then on, even if the
  // tenant-wide setting changes later.
  const [reportStyle, setReportStyle] = useState('pdf');
  // The tenant's Customer Report Style, kept separately from the per-scan choice so a
  // history row that stored no choice (report_style NULL) can still be previewed as the
  // prospect will actually see it.
  const [tenantReportStyle, setTenantReportStyle] = useState('pdf');

  // Prospect States
  const [prospectEmail, setProspectEmail] = useState("");
  const [prospectName, setProspectName] = useState("");
  const [prospectCompany, setProspectCompany] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  // Email Template States
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_PROSPECT_TEMPLATE.subject);
  const [emailBody, setEmailBody] = useState(DEFAULT_PROSPECT_TEMPLATE.body);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [templateError, setTemplateError] = useState("");
  const [templateMessage, setTemplateMessage] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: "", body: "" });

  // PDF Generation
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

  // ==================== API FUNCTIONS ====================

  const performProspectingScan = async (scanData) => {
    try {
      const response = await apifetch('s2/api/prospect/scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          target: scanData.target,
          targetType: scanData.targetType || 'domain',
          modules: scanData.modules || [],
          hibpEmails: scanData.hibpEmails || [],
          public_ip:scanData.public_ip,
          public_ip_mode: scanData.public_ip_mode,
          report_style: scanData.report_style,
          prospect_company: scanData.prospect_company || '',
          prospecting: true,
          oneTime: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to perform prospecting scan');
      }

      return await response.json();
    } catch (error) {
      console.error('Prospecting scan error:', error);
      throw error;
    }
  };

  const sendProspectReport = async (reportData) => {
    try {
      const response = await apifetch('s2/api/prospect-report/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          to: reportData.to,
          subject: reportData.subject,
          message: reportData.message,
          reportData: reportData.reportData,
          domain: reportData.domain,
          prospectName: reportData.prospectName || '',
          prospectCompany: reportData.prospectCompany || '',
          pdfBase64: reportData.pdfBase64,
          pdfName: reportData.pdfName,
          templateId: reportData.templateId || null,
          scanId: reportData.scanId || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send report');
      }

      return await response.json();
    } catch (error) {
      console.error('Send report error:', error);
      throw error;
    }
  };

  const getProspectScanHistory = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      if (filters.domain) params.append('domain', filters.domain);

      const response = await apifetch(`s2/api/prospect/history?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scan history');
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch history error:', error);
      throw error;
    }
  };

  const getProspectTemplates = async () => {
    try {
      const response = await apifetch('s2/api/prospect/templates', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      return data.templates?.filter(t => t.template_type === 'prospect') || [];
    } catch (error) {
      console.error('Fetch templates error:', error);
      throw error;
    }
  };

  const saveProspectTemplate = async (template) => {
    try {
      const isUpdate = !!template.id;
      const url = isUpdate 
        ? `s2/api/prospect/templates/${template.id}`
        : 's2/api/prospect/templates';

      const response = await apifetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: template.name,
          subject: template.subject,
          body: template.body,
          template_type: 'prospect'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save template');
      }

      return await response.json();
    } catch (error) {
      console.error('Save template error:', error);
      throw error;
    }
  };

  const deleteProspectTemplate = async (templateId) => {
    try {
      const response = await apifetch(`s2/api/prospect/templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete template');
      }

      return await response.json();
    } catch (error) {
      console.error('Delete template error:', error);
      throw error;
    }
  };

  // ==================== COMPONENT FUNCTIONS ====================

  const fetchTemplates = async () => {
    try {
      const data = await getProspectTemplates();
      setTemplates(data);
      reloadCurrentTemplate();
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      showToast('Failed to load templates', 'error');
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    setTemplateError("");
    setTemplateMessage("");

    if (!templateName.trim()) {
      setTemplateError("Template name is required");
      setSavingTemplate(false);
      return;
    }

    try {
      const data = await saveProspectTemplate({
        id: templateId,
        name: templateName,
        subject: templateSubject,
        body: templateBody
      });

      setTemplateMessage(data.message || "Template saved successfully");
      setEmailSubject(templateSubject);
      setEmailBody(templateBody);
      
      await fetchTemplates();
      
      if (data.template) {
        setSelectedTemplate(data.template.id);
        setSelectedTemplateName(data.template.name);
      }

      setTimeout(() => {
        setShowTemplateModal(false);
        setTemplateMessage("");
        resetTemplateForm();
      }, 1500);

    } catch (error) {
      setTemplateError(error.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await deleteProspectTemplate(templateToDelete);
      await fetchTemplates();
      
      if (selectedTemplate === templateToDelete) {
        setSelectedTemplate(null);
        setSelectedTemplateName("");
        setEmailSubject(DEFAULT_PROSPECT_TEMPLATE.subject);
        setEmailBody(DEFAULT_PROSPECT_TEMPLATE.body);
      }
      
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
      showToast('Template deleted successfully', 'success');
      
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const loadTemplate = (template) => {
    setSelectedTemplate(template.id);
    setSelectedTemplateName(template.name);
    setEmailSubject(template.subject || '');
    setEmailBody(template.body);
    if (results) {
      updatePreview(results, template.subject || '', template.body || '');
    }
  };

  const openTemplateEditor = (template = null) => {
    if (template) {
      setTemplateId(template.id);
      setTemplateName(template.name);
      setTemplateSubject(template.subject || '');
      setTemplateBody(template.body);
    } else {
      resetTemplateForm();
      setTemplateSubject(emailSubject);
      setTemplateBody(emailBody);
    }
    setTemplateError("");
    setTemplateMessage("");
    setShowTemplateModal(true);
  };

  const resetTemplateForm = () => {
    setTemplateId(null);
    setTemplateName("");
    setTemplateSubject("");
    setTemplateBody("");
    setTemplateError("");
    setTemplateMessage("");
  };

  const updatePreview = (scanResults, subject = emailSubject, body = emailBody) => {
    if (!scanResults) return;
    
    const riskLevel = scanResults.riskScore >= 75 ? 'LOW' :
                     scanResults.riskScore >= 60 ? 'MEDIUM' :
                     scanResults.riskScore >= 40 ? 'HIGH' : 'CRITICAL';
    
    const findingsSummary = scanResults.categories?.map(cat => {
      const findingCount = cat.findings?.length || 0;
      if (findingCount > 0) {
        const critical = cat.findings?.filter(f => f.severity?.toLowerCase() === 'critical').length || 0;
        const high = cat.findings?.filter(f => f.severity?.toLowerCase() === 'high').length || 0;
        return `• ${cat.name}: ${findingCount} findings (${critical} critical, ${high} high)`;
      }
      return null;
    }).filter(Boolean).join('\n') || 'No significant findings detected.';

    const previewSubject = subject
      .replace(/{domain}/g, domain)
      .replace(/{prospectName}/g, prospectName || 'Customer')
      .replace(/{prospectCompany}/g, prospectCompany || 'Company')
      .replace(/{riskScore}/g, scanResults.riskScore || 'N/A')
      .replace(/{riskLevel}/g, riskLevel)
      .replace(/{totalFindings}/g, scanResults.totalFindings || '0');

    const previewBody = body
      .replace(/{domain}/g, domain)
      .replace(/{prospectName}/g, prospectName || 'Customer')
      .replace(/{prospectCompany}/g, prospectCompany || 'Company')
      .replace(/{riskScore}/g, scanResults.riskScore || 'N/A')
      .replace(/{riskLevel}/g, riskLevel)
      .replace(/{totalFindings}/g, scanResults.totalFindings || '0')
      .replace(/{findingsSummary}/g, findingsSummary);

    setPreviewContent({ subject: previewSubject, body: previewBody });
  };

  const loadScanHistory = async () => {
    try {
      const data = await getProspectScanHistory({ limit: 50 });
      setScanHistory(data.scans || []);
    } catch (err) {
      console.error('Failed to load scan history:', err);
    }
  };

  // Seed the report-style selector from the tenant's Customer Report Style so the default
  // matches what the rest of the platform does; the salesperson can override per scan.
  const loadTenantReportStyle = async () => {
    try {
      const response = await apifetch('/s1/api/tenant', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      // GET /s1/api/tenant returns the tenant row flat (auth.js: res.json(tenantInfo)),
      // with report_style already normalised to 'pdf' | 'slide_deck'.
      const data = await response.json();
      if (data?.report_style === 'slide_deck' || data?.report_style === 'pdf') {
        setReportStyle(data.report_style);
        setTenantReportStyle(data.report_style);
      }
    } catch (err) {
      // Non-fatal — fall back to the 'pdf' default already in state.
      console.error('Could not load tenant report style:', err);
    }
  };

  useEffect(() => {
    fetchTemplates();
    loadScanHistory();
    loadTenantReportStyle();
  }, []);

  const toggleModule = (m) =>
    setModules((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));

  const allSelected = modules.length === MODULES.length;
  const toggleAllModules = () => {
    setModules(allSelected ? [] : MODULES.map(m => m.id));
  };

  const validateEmail = (email) => {
    return EMAIL_REGEX.test(email);
  };

  // Handle scan
  const handleScan = async (e) => {
    e.preventDefault();
    
    if (!domain || !modules.length) {
      showToast('Please enter a domain and select modules', 'error');
      return;
    }

    setLoading(true);
    setError("");
    setResults(null);
    setShowResults(false);
    setEmailSuccess(false);

    try {
      const data = await performProspectingScan({
        target: domain,
        targetType: 'domain',
        modules,
        hibpEmails: [],
        report_style: reportStyle,
        prospect_company: prospectCompany.trim().slice(0, 255),
        ...publicIpRequest({ target: domain, enabled: modules.includes("publicIp"), mode: publicIpMode, manual: publicIp }),
      });

      setResults(data);
      setScanId(data.scanId || null);
      setShowResults(true);
      updatePreview(data);
      
      // Reload history
      await loadScanHistory();
      showToast('Scan completed successfully!', 'success');

    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // What will the prospect actually see for a given scan? The per-scan choice wins; a
  // scan saved before the selector existed (or with no choice) follows the tenant's
  // Customer Report Style, exactly like GET /s2/api/download/report/:scanId resolves it.
  const effectiveReportStyle = (scanStyle) =>
    (scanStyle === 'slide_deck' || scanStyle === 'pdf') ? scanStyle : tenantReportStyle;

  // Open the slide deck the prospect will receive. Choosing "Slide deck presentation"
  // only changed what the emailed link renders — there was no way for the salesperson to
  // look at it before sending. This opens the very same presentation, in its own tab
  // (full-page-height slides don't fit inside the dashboard's page flow), reusing the
  // internal-preview route rather than a second deck renderer.
  const openSlideDeck = (scanResults, scanDomain) => {
    if (!scanResults) {
      showToast('No results to preview', 'error');
      return;
    }
    try {
      sessionStorage.setItem(INTERNAL_REPORT_PREVIEW_KEY, JSON.stringify({
        results: { domain: scanDomain, ...scanResults },
        theme: isDarkMode ? 'dark' : 'light'
      }));
    } catch (e) {
      showToast('Could not open the slide deck (report may be too large)', 'error');
      return;
    }
    // No 'noopener': sessionStorage is only copied into a tab opened via window.open()
    // when it stays in the same browsing-context group, and we need that copy to read
    // the data back. Safe — we only ever navigate to our own route.
    window.open('/internal-report-preview', '_blank');
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!results) {
      showToast('No results to export', 'error');
      return;
    }

    setExporting(true);
    showToast('Generating security report...', 'info');

    try {
      const scanDomain = results.domain || results.target || domain || 'domain';
      if(effectiveReportStyle(reportStyle) === 'slide_deck'){

        await generateSecurityPDFSlide(results, scanDomain, {
          theme: isDarkMode ? 'dark' : 'light',
          prospectName: prospectName || '',
          prospectCompany: prospectCompany || '',
          isCyberMode: false,
          saveFile: true,
          fileName: `security-report-${scanDomain}-${Date.now()}.pdf`,
          onProgress: (step, message) => {
            console.log(`${step}%: ${message}`);
          }
        });
      }else{
        await generateSecurityPDF(results, scanDomain, {
          theme: isDarkMode ? 'dark' : 'light',
          prospectName: prospectName || '',
          prospectCompany: prospectCompany || '',
          isCyberMode: false,
          saveFile: true,
          fileName: `security-report-${scanDomain}-${Date.now()}.pdf`,
          onProgress: (step, message) => {
            console.log(`${step}%: ${message}`);
          }
        });

      }

      showToast('Security report generated successfully', 'success');
      
    } catch (err) {
      console.error('PDF export failed:', err);
      showToast('Failed to generate PDF: ' + err.message, 'error');
    }
    
    setExporting(false);
  };

  const reloadCurrentTemplate = () => {
    if (selectedTemplate) {
       loadTemplate(templates.find(t => t.id === selectedTemplate));
       updatePreview(results);
    }
  };

  //reload current template content when email,name and company change
  useEffect(() => {
    reloadCurrentTemplate();
  }, [prospectName, prospectCompany]);


  const getEmailContent = () => {
    const riskLevel = results?.riskScore >= 75 ? 'LOW' :
                     results?.riskScore >= 60 ? 'MEDIUM' :
                     results?.riskScore >= 40 ? 'HIGH' : 'CRITICAL';
    
    const findingsSummary = results?.categories?.map(cat => {
      const findingCount = cat.findings?.length || 0;
      if (findingCount > 0) {
        const critical = cat.findings?.filter(f => f.severity?.toLowerCase() === 'critical').length || 0;
        const high = cat.findings?.filter(f => f.severity?.toLowerCase() === 'high').length || 0;
        return `• ${cat.name}: ${findingCount} findings (${critical} critical, ${high} high)`;
      }
      return null;
    }).filter(Boolean).join('\n') || 'No significant findings detected.';

    // Generate a unique download link (this would be your actual PDF download URL)
    // You can use a pre-signed S3 URL or a temporary download endpoint
    const downloadLink = `${window.location.origin}/prospect/download/${scanId || results?.scanId || 'temp'}`;

    const subject = emailSubject
      .replace(/{domain}/g, domain)
      .replace(/{prospectName}/g, prospectName || 'Customer')
      .replace(/{prospectCompany}/g, prospectCompany || 'Company')
      .replace(/{riskScore}/g, results?.riskScore || 'N/A')
      .replace(/{riskLevel}/g, riskLevel)
      .replace(/{totalFindings}/g, results?.totalFindings || '0');

    const body = emailBody
      .replace(/{domain}/g, domain)
      .replace(/{prospectName}/g, prospectName || 'Customer')
      .replace(/{prospectCompany}/g, prospectCompany || 'Company')
      .replace(/{riskScore}/g, results?.riskScore || 'N/A')
      .replace(/{riskLevel}/g, riskLevel)
      .replace(/{totalFindings}/g, results?.totalFindings || '0')
      .replace(/{findingsSummary}/g, findingsSummary)
      .replace(/{downloadLink}/g, downloadLink);

    return { subject, body };
  };

  // Get mailto URL with download link (NO ATTACHMENT)
  const getMailToUrl = (provider) => {
    const emailList = prospectEmail;
    const { subject, body } = getEmailContent();
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    
    switch(provider) {
      case 'gmail':
        return `https://mail.google.com/mail/?view=cm&fs=1&to=${emailList}&su=${encodedSubject}&body=${encodedBody}`;
      case 'outlook':
        return `https://outlook.live.com/mail/0/deeplink/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      case 'outlook-office':
        return `https://outlook.office.com/mail/deeplink/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      case 'yahoo':
        return `https://mail.yahoo.com/mail/compose?to=${emailList}&subject=${encodedSubject}&body=${encodedBody}`;
      default:
        return `mailto:${emailList}?subject=${encodedSubject}&body=${encodedBody}`;
    }
  };

  // Open email client with download link
  const openEmailClient = (provider) => {
    if (!prospectEmail) {
      setEmailError("Please enter a recipient email address.");
      return;
    }
    
    if (!validateEmail(prospectEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setEmailError("");
    const url = getMailToUrl(provider);
    window.open(url, "_blank");
  };

  // Handle send via API (with PDF attachment)
  const handleSendViaAPI1 = async () => {
    if (!results || !prospectEmail) {
      showToast('Please enter a recipient email', 'error');
      return;
    }

    if (!validateEmail(prospectEmail)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setSendingReport(true);
    setEmailError("");

    try {
      // Generate PDF and convert to base64
      showToast('Generating PDF...', 'info');
      
      const pdfBlob = await generateSecurityPDF(results, domain, {
        theme: isDarkMode ? 'dark' : 'light',
        prospectName: prospectName || '',
        prospectCompany: prospectCompany || '',
        isCyberMode: false,
        saveFile: false,
        onProgress: (step, message) => {
          console.log(`${step}%: ${message}`);
        }
      });

      // Convert blob to base64
      const reader = new FileReader();
      const pdfBase64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      const { subject, body } = getEmailContent();

      await sendProspectReport({
        to: prospectEmail,
        subject: subject,
        message: body,
        reportData: results,
        domain: domain,
        prospectName: prospectName,
        prospectCompany: prospectCompany,
        pdfBase64: pdfBase64,
        pdfName: `security-report-${domain}-${Date.now()}.pdf`,
        templateId: selectedTemplate,
        scanId: scanId
      });

      setEmailSuccess(true);
      showToast('Report sent successfully!', 'success');
      
      // Reload history
      await loadScanHistory();
      
      setTimeout(() => {
        setEmailSuccess(false);
        setProspectEmail("");
        setProspectName("");
        setProspectCompany("");
        setShowResults(false);
        setResults(null);
        setActiveTab("history");
      }, 3000);

    } catch (error) {
      setEmailError(error.message);
      showToast(error.message, 'error');
    } finally {
      setSendingReport(false);
    }
  };
  //replace api call with direct gmail
  const handleSendViaAPI = async () => {
    openEmailClient('default');
  };

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  const severityVariant = (severity) => {
    if (severity === "critical" || severity < 40) return "danger";
    if (severity === "high" || severity < 50) return "warning";
    if (severity === "medium" || severity < 70) return "info";
    return "success";
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'sent':
        return <Badge bg="success"><Mail size={12} className="me-1" />Sent</Badge>;
      case 'pending':
        return <Badge bg="warning"><Clock size={12} className="me-1" />Pending</Badge>;
      case 'failed':
        return <Badge bg="danger"><AlertTriangle size={12} className="me-1" />Failed</Badge>;
      case 'completed':
        return <Badge bg="info"><Check size={12} className="me-1" />Completed</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  // ==================== RENDER ====================

  return (
    <div className={`prospecting-mode ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Toast */}
      {toast.show && (
        <div className={`toast-container ${isDarkMode ? 'dark' : ''}`}>
          <div className={`toast-item alert alert-${toast.type} ${isDarkMode ? 'bg-dark text-light' : ''}`}>
            {toast.message}
          </div>
        </div>
      )}

     <Tabs
  activeKey={activeTab}
  onSelect={(k) => setActiveTab(k)}
  className={`mb-4 ${isDarkMode ? 'nav-dark' : ''}`}
>
  <Tab eventKey="new-scan" title={
    <span><Target size={16} className="me-2" />New Scan</span>
  }>
    <Card className={`border-0 shadow-lg ${isDarkMode ? "bg-dark text-light" : "bg-white"}`}>
      <Card.Body>
        <div className="d-flex align-items-center mb-4">
          <div className={`p-3 rounded-circle me-3 ${isDarkMode ? 'bg-primary' : 'bg-primary bg-opacity-10'}`}>
            <Target size={28} className={isDarkMode ? 'text-white' : 'text-primary'} />
          </div>
          <div>
            <h4 className="mb-1">Prospecting Mode</h4>
            <p className={`mb-0 ${isDarkMode ? 'text-light-50' : 'text-muted'}`}>
              Scan websites without creating customer orgs. Download PDF or send reports to prospects.
            </p>
          </div>
          <Badge bg="primary" className="ms-auto">
            <FileText size={12} className="me-1" />
            PDF Reports
          </Badge>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleScan}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <Shield size={16} className="me-2" />
              Domain to Scan *
            </Form.Label>
            <Form.Control
              placeholder="example.com"
              value={domain}
              disabled={loading}
              onChange={(e) => setDomain(e.target.value.toLowerCase().trim())}
              className={isDarkMode ? "bg-dark text-light border-0" : ""}
            />
            <Form.Text className={`${isDarkMode ? "bg-dark text-white" : "text-muted"}`}>
              Enter the domain you want to scan for security vulnerabilities
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="prospect-scan-company">
            <Form.Label className="fw-semibold">Prospect company name (optional)</Form.Label>
            <Form.Control
              placeholder="Example Company Ltd"
              value={prospectCompany}
              maxLength={255}
              disabled={loading}
              onChange={(e) => setProspectCompany(e.target.value)}
              className={isDarkMode ? "bg-dark text-light border-0" : ""}
            />
            <Form.Text className={isDarkMode ? "text-light" : "text-muted"}>
              Optional: helps report titles and automatic IP discovery match the prospect's registered company. Without it, discovery uses domain-based evidence.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Select Modules</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              <Badge
                pill
                bg={allSelected ? "danger" : "success"}
                style={{ cursor: "pointer" }}
                onClick={toggleAllModules}
                className="module-badge"
              >
                {allSelected ? "Unselect All" : "Select All"}
              </Badge>
              {MODULES.map((m) => (
                <Badge
                  key={m.id}
                  pill
                  bg={modules.includes(m.id) ? "primary" : "secondary"}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleModule(m.id)}
                  className="module-badge"
                >
                  {m.icon} {m.label}
                </Badge>
              ))}
            </div>
          </Form.Group>
         <PublicIpOptions target={domain} enabled={modules.includes("publicIp")}
                           mode={publicIpMode} onModeChange={setPublicIpMode}
                           value={publicIp} onChange={setPublicIp} disabled={loading} />

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FileText size={16} className="me-2" />
              Report Style
            </Form.Label>
            <Form.Select
              value={reportStyle}
              disabled={loading}
              onChange={(e) => setReportStyle(e.target.value)}
              className={isDarkMode ? "bg-dark text-light border-0" : ""}
            >
              <option value="pdf">PDF / Single-page report</option>
              <option value="slide_deck">Slide deck presentation</option>
            </Form.Select>
            <Form.Text className={`${isDarkMode ? "bg-dark text-white" : "text-muted"}`}>
              How this prospect will see the report when they open your link. Defaults to
              your tenant's Customer Report Style; the choice is saved with this scan.
            </Form.Text>
          </Form.Group>

          <Button
            type="submit"
            className="w-100 mt-2"
            disabled={!domain || !modules.length || loading}
            variant="primary"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Scanning...
              </>
            ) : (
              <>
                <Activity size={18} className="me-2" />
                Start Prospecting Scan
              </>
            )}
          </Button>
        </Form>

        {/* ==================== INLINE RESULTS ==================== */}
        {showResults && results && (
          <div className={`mt-4 p-3 rounded ${isDarkMode ? 'bg-dark text-light' : 'bg-light'}`}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 text-success">
                <Check size={20} className="me-2" />
                Scan Complete!
              </h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setShowResults(false);
                  setResults(null);
                }}
              >
                <X size={14} />
              </Button>
            </div>

            {/* Summary Cards */}
            <Row className="mb-3">
              <Col xs={6} md={3} className="mb-2">
                <div className={`p-2 rounded text-center ${isDarkMode ? 'bg-dark' : 'bg-white'}`}>
                  <div className="h5 mb-0">{results.riskScore}/100</div>
                  <small className={`${isDarkMode ? "text-light" : "text-muted"}`}>Risk Score</small>
                </div>
              </Col>
              <Col xs={6} md={3} className="mb-2">
                <div className={`p-2 rounded text-center ${isDarkMode ? 'bg-dark' : 'bg-white'}`}>
                  {/* Non-passing findings — the ones the score actually deducted for.
                      This card previously read results.totalFindings, which the scanner
                      never returned, so it showed 0 next to every score. */}
                  <div className="h5 mb-0 text-warning">{results.issueCount ?? 0}</div>
                  <small className={`${isDarkMode ? "text-light" : "text-muted"}`}>Issues</small>
                </div>
              </Col>
              <Col xs={6} md={3} className="mb-2">
                <div className={`p-2 rounded text-center ${isDarkMode ? 'bg-dark' : 'bg-white'}`}>
                  <div className="h5 mb-0 text-danger">
                    {results.severityCounts?.critical ?? results.criticalCount ?? 0}
                  </div>
                  <small className={`${isDarkMode ? "text-light" : "text-muted"}`}>Critical</small>
                </div>
              </Col>
              <Col xs={6} md={3} className="mb-2">
                <div className={`p-2 rounded text-center ${isDarkMode ? 'bg-dark' : 'bg-white'}`}>
                  <div className="h5 mb-0 text-success">{results.passedCount || 0}</div>
                  <small className={`${isDarkMode ? "text-light" : "text-muted"}`}>Passed</small>
                </div>
              </Col>
            </Row>

            <hr />

            {/* Recipient Information */}
            <h6 className="mb-2">Send Report to Prospect</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">
                    <Mail size={14} className="me-1" />
                    Email Address *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="prospect@company.com"
                    value={prospectEmail}
                    onChange={(e) => {
                      setProspectEmail(e.target.value);
                      setEmailError("");
                    }}
                    isInvalid={prospectEmail && !validateEmail(prospectEmail)}
                    className={isDarkMode ? "bg-dark text-light" : ""}
                    size="sm"
                  />
                  {prospectEmail && !validateEmail(prospectEmail) && (
                    <Form.Text className="text-danger">Invalid email</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">
                    <User size={14} className="me-1" />
                    Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="John Doe"
                    value={prospectName}
                    onChange={(e) => setProspectName(e.target.value)}
                    className={isDarkMode ? "bg-dark text-light" : ""}
                    size="sm"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">
                    <Building size={14} className="me-1" />
                    Company
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Acme Inc."
                    value={prospectCompany}
                    onChange={(e) => setProspectCompany(e.target.value)}
                    className={isDarkMode ? "bg-dark text-light" : ""}
                    size="sm"
                  />
                </Form.Group>
              </Col>
            </Row>

            {emailError && (
              <Alert variant="danger" className="mt-2 py-1 small">
                {emailError}
              </Alert>
            )}

            {emailSuccess && (
              <Alert variant="success" className="mt-2 py-1 small">
                <Check size={14} className="me-1" />
                Report sent successfully to {prospectEmail} with download link!
              </Alert>
            )}

            {/* Template Selector */}
            <div className="d-flex justify-content-between align-items-center mt-2">
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    <Folder size={14} className="me-1" />
                    {selectedTemplate ? 'Template' : 'Load Template'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={isDarkMode ? "bg-dark" : ""}>
                    <Dropdown.Item  
                      onClick={() => {
                        setSelectedTemplate(null);
                        setEmailSubject(DEFAULT_PROSPECT_TEMPLATE.subject);
                        setEmailBody(DEFAULT_PROSPECT_TEMPLATE.body);
                        updatePreview(results);
                      }}
                      className={isDarkMode ? "text-light" : ""}
                    >
                      Default Template
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    {templates.map((template) => (
                      <Dropdown.Item 
                        key={template.id}
                        onClick={() => {
                          loadTemplate(template);
                          updatePreview(results);
                        }}
                        className={`${isDarkMode ? "text-light" : ""} d-flex justify-content-between align-items-center`}
                      >
                        <span>{template.name}</span>
                        <div className="d-flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTemplateEditor(template);
                            }}
                          >
                            <Edit2 size={12} />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTemplateToDelete(template.id);
                              setShowDeleteConfirm(true);
                            }}
                          >
                            <Trash size={12} />
                          </Button>
                        </div>
                      </Dropdown.Item>
                    ))}
                    {templates.length === 0 && (
                      <Dropdown.Item disabled>No saved templates</Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => openTemplateEditor()}
                >
                  <Plus size={14} className="me-1" />
                  New
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  disabled={!prospectEmail}
                >
                  <Eye size={14} className="me-1" />
                  Preview
                </Button>
              </div>
              <div className="d-flex gap-1 flex-wrap mt-2 mt-sm-0">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadPDF}
                  disabled={exporting}
                >
                  {exporting ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <Download size={14} className="me-1" />
                      PDF
                    </>
                  )}
                </Button>
                {effectiveReportStyle(reportStyle) === 'slide_deck' && (
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => openSlideDeck(results, results?.domain || domain)}
                    title="Open the slide deck exactly as the prospect will see it"
                  >
                    <Eye size={14} className="me-1" />
                    View Slide Deck
                  </Button>
                )}
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleSendViaAPI}
                  disabled={sendingReport || !prospectEmail || !validateEmail(prospectEmail)}
                >
                  {sendingReport ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <Send size={14} className="me-1" />
                      Send Link
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Email Provider Buttons - with download link in body */}
            <div className="mt-3">
              <small className="text-muted d-block mb-2">
                <FileText size={12} className={`${isDarkMode ? "me-1 text-light" : "me-1 text-muted"}`} />
                {effectiveReportStyle(reportStyle) === 'slide_deck'
                  ? 'Email includes a link that opens the slide deck presentation'
                  : 'Email includes a download link to the PDF report'}
              </small>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => openEmailClient('gmail')}
                  disabled={!prospectEmail || !validateEmail(prospectEmail)}
                >
                  <Mail size={12} className="me-1" /> Gmail
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => openEmailClient('outlook')}
                  disabled={!prospectEmail || !validateEmail(prospectEmail)}
                >
                  <Mail size={12} className="me-1" /> Outlook.com
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => openEmailClient('outlook-office')}
                  disabled={!prospectEmail || !validateEmail(prospectEmail)}
                >
                  <Mail size={12} className="me-1" /> Microsoft 365
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => openEmailClient('yahoo')}
                  disabled={!prospectEmail || !validateEmail(prospectEmail)}
                >
                  <Mail size={12} className="me-1" /> Yahoo
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => openEmailClient('default')}
                  disabled={!prospectEmail || !validateEmail(prospectEmail)}
                >
                  <Mail size={12} className="me-1" /> Default
                </Button>
              </div>
            </div>

            <Form.Text className={`d-block mt-2 small ${isDarkMode ? "text-light" : "text-muted"}`}>
              <span className={`${isDarkMode ? "text-light" : ""} fw-bold`}>Placeholders:</span> {'{domain}'}, {'{prospectName}'}, {'{riskScore}'}, {'{downloadLink}'}
            </Form.Text>
          </div>
        )}
      </Card.Body>
    </Card>
  </Tab>

  <Tab eventKey="history" title={
  <span><Clock size={16} className="me-2" />History</span>
}>
  <Card className={`border-0 shadow-lg ${isDarkMode ? "bg-dark text-light" : "bg-white"}`}>
    <Card.Body>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">Scan History</h5>
          <p className={`mb-0 small ${isDarkMode ? "text-light-50" : "text-muted"}`}>
            {scanHistory.length} scan{scanHistory.length !== 1 ? 's' : ''} on record
          </p>
        </div>
        <Button
          variant={isDarkMode ? "outline-light" : "outline-primary"}
          size="sm"
          onClick={loadScanHistory}
        >
          <RefreshCw size={14} className="me-1" />
          Refresh
        </Button>
      </div>

      {scanHistory.length === 0 ? (
        <div className="text-center py-5">
          <div className="mb-3 opacity-25">
            <FileText size={64} />
          </div>
          <p className={`${isDarkMode ? "text-light" : "text-muted"}`}>No scans performed yet</p>
          <p className={`${isDarkMode ? "text-light" : "text-muted"} small`}>
            Start your first prospecting scan by going to the "New Scan" tab
          </p>
        </div>
      ) : (
        <div className="history-list d-flex flex-column gap-2">
          {scanHistory.map((item) => {
            const score = item.riskScore ?? null;
            const variant = severityVariant(score);
            const style = item.reportStyle || item.report_style;
            const isThisRowGenerating = isGeneratingPDF && generatingId === item.scanId;

            return (
              <div
                key={item.scanId || item.id}
                className={`history-row d-flex align-items-center gap-3 p-3 rounded-3 ${
                  isDarkMode ? "history-row-dark" : "history-row-light"
                }`}
              >
                {/* Risk score ring */}
                <div className={`score-ring score-ring-${variant} flex-shrink-0`}>
                  <span>{score ?? '—'}</span>
                </div>

                {/* Domain + date */}
                <div className="flex-shrink-0" style={{ minWidth: 160 }}>
                  <div className="fw-semibold text-truncate">{item.domain}</div>
                  <div className={`small ${isDarkMode ? "text-light-50" : "text-muted"}`}>
                    {new Date(item.createdAt || item.date).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric'
                    })}
                  </div>
                </div>

                {/* Badges */}
                <div className="d-flex flex-wrap gap-2 flex-grow-1">
                  <Badge bg={isDarkMode ? "secondary" : "light"} text={isDarkMode ? undefined : "dark"} className="fw-normal">
                    <BarChart2 size={12} className="me-1" />
                    {item.totalFindings || 0} findings
                  </Badge>
                  {style === 'slide_deck' ? (
                    <Badge bg="info" className="fw-normal">
                      <Monitor size={12} className="me-1" />Slide deck
                    </Badge>
                  ) : style === 'pdf' ? (
                    <Badge bg="secondary" className="fw-normal">
                      <FileText size={12} className="me-1" />PDF
                    </Badge>
                  ) : (
                    <Badge
                      bg={isDarkMode ? "secondary" : "light"}
                      text={isDarkMode ? undefined : "dark"}
                      className="fw-normal"
                      title="No per-scan choice — uses your tenant default"
                    >
                      Default
                    </Badge>
                  )}
                  {getStatusBadge(item.status)}
                </div>

                {/* Actions */}
                <div className="d-flex gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant={isDarkMode ? "outline-light" : "outline-secondary"}
                    className="action-btn"
                    onClick={() => {
                      setResults(item.results);
                      setDomain(item.domain);
                      setProspectEmail(item.prospectEmail || '');
                      setProspectName(item.prospectName || '');
                      setProspectCompany(item.prospectCompany || '');
                      setActiveTab("new-scan");
                      setShowResults(true);
                      updatePreview(item);
                    }}
                    title="View Scan"
                  >
                    <Eye size={14} />
                  </Button>

                  <Button
                    size="sm"
                    variant={isDarkMode ? "outline-light" : "outline-secondary"}
                    className="action-btn"
                    disabled={isThisRowGenerating}
                    onClick={async () => {
                      setIsGeneratingPDF(true);
                      setGeneratingId(item.scanId);
                      try {
                        await generateSecurityPDF(item.results, item.domain, {
                          theme: isDarkMode ? 'dark' : 'light',
                          prospectName: item.prospectName || '',
                          prospectCompany: item.prospectCompany || '',
                          saveFile: true,
                          fileName: `security-report-${item.domain}-${Date.now()}.pdf`
                        });
                        showToast('PDF downloaded successfully', 'success');
                      } catch (err) {
                        showToast('Failed to download PDF: ' + err.message, 'error');
                      }
                      setIsGeneratingPDF(false);
                      setGeneratingId(null);
                    }}
                    title="Download PDF"
                  >
                    {isThisRowGenerating ? <Spinner size="sm" /> : <Download size={14} />}
                  </Button>

                  {effectiveReportStyle(style) === 'slide_deck' && (
                    <Button
                      size="sm"
                      variant={isDarkMode ? "outline-light" : "outline-secondary"}
                      className="action-btn"
                      onClick={() => openSlideDeck(item.results, item.domain)}
                      title="Open the slide deck this prospect sees"
                    >
                      <Monitor size={14} />
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="success"
                    className="action-btn"
                    onClick={() => {
                      setResults(item);
                      setDomain(item.domain);
                      setProspectEmail(item.prospectEmail || '');
                      setProspectName(item.prospectName || '');
                      setProspectCompany(item.prospectCompany || '');
                      setActiveTab("new-scan");
                      setShowResults(true);
                      updatePreview(item);
                    }}
                    title="Send Report"
                  >
                    <Send size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card.Body>
  </Card>
</Tab>

  <Tab eventKey="templates" title={
    <span><Folder size={16} className="me-2" />Templates</span>
  }>
    <Card className={`border-0 shadow-lg ${isDarkMode ? "bg-dark text-light" : "bg-white"}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Email Templates</h5>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => openTemplateEditor()}
          >
            <Plus size={14} className="me-1" />
            New Template
          </Button>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-5">
            <div className="display-1 mb-3 opacity-25">
              <Folder size={64} />
            </div>
            <p className={`${isDarkMode ? "text-light" : "text-muted"}`}>No templates saved yet</p>
            <p className={`${isDarkMode ? "text-light" : "text-muted"} small`}>
              Create templates to quickly send professional security reports
            </p>
          </div>
        ) : (
          <div className="row">
            {templates.map((template) => (
              <div key={template.id} className="col-md-6 col-lg-4 mb-3">
                <Card className={`${isDarkMode ? 'bg-dark text-light' : 'bg-light'}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className={`mb-2 ${isDarkMode ? "text-light" : "text-dark"}`}>{template.name}</h6>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openTemplateEditor(template)}
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            setTemplateToDelete(template.id);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                    <p className={`small mb-2 ${isDarkMode ? "text-white" : "text-muted"}`}>
                      {template.subject || 'No subject'}
                    </p>
                    <Button
                      size="sm"
                      variant="success" 
                      onClick={() => {
                        loadTemplate(template);
                        setActiveTab("new-scan");
                        showToast(`Template "${template.name}" loaded`, 'success');
                      }}
                    >
                      <Mail size={12} className="me-1" />
                      Use Template
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  </Tab>
</Tabs>

      {/* ==================== MODALS ==================== */}

      {/* Email Template Modal */}
      <Modal 
        show={showTemplateModal} 
        onHide={() => {
          setShowTemplateModal(false);
          resetTemplateForm();
        }} 
        centered 
        size="lg"
        data-bs-theme={isDarkMode ? "dark" : "light"}
        contentClassName={isDarkMode ? "bg-dark" : ""}
      >
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
          <Modal.Title className={isDarkMode ? "text-light" : ""}>
            <Save className="me-2" size={20} />
            {templateId ? 'Update' : 'Save'} Prospect Email Template
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={saveTemplate}>
          <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
            {templateError && (
              <Alert variant="danger" onClose={() => setTemplateError("")} dismissible>
                {templateError}
              </Alert>
            )}
            {templateMessage && (
              <Alert variant="success" onClose={() => setTemplateMessage("")} dismissible>
                {templateMessage}
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Template Name *</Form.Label>
              <Form.Control
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard Prospect Email"
                required
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                Use {'{domain}'}, {'{prospectName}'}, {'{prospectCompany}'}, {'{riskScore}'}, {'{riskLevel}'}, {'{totalFindings}'} as placeholders
              </Form.Text>
              <Form.Control
                type="text"
                value={templateSubject}
                onChange={(e) => setTemplateSubject(e.target.value)}
                placeholder="Email subject line..."
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Body Content</Form.Label>
              <Form.Text className={`${isDarkMode ? "text-light" : "text-muted"} d-block mb-1`}>
                Use {'{domain}'}, {'{prospectName}'}, {'{prospectCompany}'}, {'{riskScore}'}, {'{riskLevel}'}, {'{totalFindings}'}, {'{findingsSummary}'}, {'{downloadLink}'} as placeholders
              </Form.Text>
              <Form.Control
                as="textarea"
                rows={10}
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                placeholder="Write your email template here... Use {downloadLink} to include the PDF download URL"
                className={isDarkMode ? "bg-dark text-light" : ""}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
            <Button variant="secondary" onClick={() => {
              setShowTemplateModal(false);
              resetTemplateForm();
            }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={savingTemplate || !templateName.trim()}
            >
              {savingTemplate ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                templateId ? 'Update Template' : 'Save Template'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>


      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
        }}
        centered
        data-bs-theme={isDarkMode ? "dark" : "light"}
        contentClassName={isDarkMode ? "bg-dark" : ""}
      >
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
          <Modal.Title className={isDarkMode ? "text-light" : ""}>
            Delete Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
          <p>Are you sure you want to delete this template? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
          <Button variant="secondary" onClick={() => {
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
          }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteTemplate}>
            <Trash size={14} className="me-1" />
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal 
        show={showPreview} 
        onHide={() => setShowPreview(false)} 
        centered 
        size="lg"
        data-bs-theme={isDarkMode ? "dark" : "light"}
        contentClassName={isDarkMode ? "bg-dark" : ""}
      >
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light" : ""}>
          <Modal.Title className={isDarkMode ? "text-light" : ""}>
            <Eye className="me-2" size={20} />
            Email Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? "bg-dark text-light" : ""}>
          <div className="mb-3">
            <strong>To:</strong>
            <div className="d-flex flex-wrap gap-1 mt-1">
              <Badge bg="primary">{prospectEmail || 'No email entered'}</Badge>
            </div>
          </div>
          
          <div className="mb-3">
            <strong>Subject:</strong>
            <div className={`p-2 rounded mt-1 ${isDarkMode ? "bg-secondary" : "bg-light"}`}>
              {previewContent.subject || emailSubject}
            </div>
          </div>
          
          <div className="mb-3">
            <strong>Message Body:</strong>
            <div className={`p-3 border rounded mt-1 ${isDarkMode ? "bg-secondary" : "bg-light"}`} 
                 style={{ whiteSpace: "pre-wrap" }}>
              {previewContent.body || emailBody}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={isDarkMode ? "bg-dark text-light" : ""}>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
          <Button 
            variant="success" 
            onClick={() => {
              setShowPreview(false);
            }}
          >
            <Send size={14} className="me-1" />
            Send Email
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProspectingMode;